import { useCallback } from "react";
import { Button } from "@mui/material";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import { FormikAddAccount } from "hooks/useAccountData";
import Summary from "components/common/Summary";
import { Stack } from "components/layout/reusableLayouts";
import { fullName } from "utils/fullName";

interface NewAccountReviewDrawerProps {
  open: boolean;
  formik: FormikProps<FormikAddAccount>;
  handleCloseDrawer: (refresh?: boolean) => void;
  onBack: () => void;
  /** Account create in flight. Gates the overlay and every close path. */
  savingAccount?: boolean;
}

/** The last step of adding an account: what will be created, before it is saved. */
const NewAccountReviewDrawer = ({
  open,
  formik,
  handleCloseDrawer,
  onBack,
  savingAccount = false,
}: NewAccountReviewDrawerProps) => {
  const { t } = useTranslation();

  const accountItems = [
    {
      title: t("platform.accountName"),
      value: formik.values.account.businessLegalName,
    },
  ];
  const userItems = [
    { title: t("Name"), value: fullName(formik.values.user) },
    { title: t("Email"), value: formik.values.user.email, fullWidth: true },
    { title: t("Phone"), value: formik.values.user.phone },
    { title: t("Address"), value: formik.values.user.address?.addressName },
  ];

  const handleClose = useCallback(() => {
    if (savingAccount) return;
    handleCloseDrawer(false);
  }, [savingAccount, handleCloseDrawer]);

  return (
    <Drawer
      loading={savingAccount}
      title={t("platform.reviewAccount")}
      open={open}
      onClose={handleClose}
      footer={
        <>
          <Button
            variant="primary-outlined"
            disabled={savingAccount}
            onClick={onBack}
          >
            {t("Back")}
          </Button>
          <Button
            variant="primary-filled"
            disabled={savingAccount}
            onClick={() => formik.submitForm()}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <Stack>
        <Summary items={accountItems} disableContainer />
        <Summary items={userItems} disableContainer />
      </Stack>
    </Drawer>
  );
};

export default NewAccountReviewDrawer;
